import React from 'react';
import classnames from 'classnames';
import { DropdownItemProps } from '@instinet-analytics/semantic-ui-components';
import { Dropdown } from '@instinet-analytics/semantic-ui-components';
import {
    BoundarySign,
    ExactSign,
    FilterValueType,
    signTextMap,
} from 'utils/filter/filter';
import NumberControl from 'utils/components/FiltersModal/comparison/NumberControl';
import StringControl from 'utils/components/FiltersModal/comparison/StringControl';
import TimeControl from 'utils/components/FiltersModal/comparison/TimeControl';
import DateControl, {
    SupportedDateComparisonValue,
} from 'utils/components/FiltersModal/comparison/DateControl';
import 'utils/components/FiltersModal/comparison/ComparisonControl.scss';
import {
    isComparisonValueValid,
    SupportedComparisonValue,
} from 'utils/components/FiltersModal/comparison/FilterComparison';
import EnumControl from 'utils/components/FiltersModal/comparison/EnumControl';
import { EnumMap } from '@instinet-analytics/core-lib';
import { ValueType } from '@instinet-analytics/semantic-ui-components';

interface ComparisonControlProps {
    columnName?: string | null;
    availableSigns: (ExactSign | BoundarySign)[];
    valueType: FilterValueType;
    sign: ExactSign | BoundarySign | undefined;
    isExactOrFirstInRange?: boolean;
    value?: SupportedComparisonValue;
    enumMap?: EnumMap;
    onSignChange: (sign: ExactSign | BoundarySign) => void;
    onValueChange: (value: SupportedComparisonValue) => void;
    onInputEnter: () => void;
    error?: boolean;
    errorMessage?: string;
}

export default function ComparisonControl(props: ComparisonControlProps) {
    const {
        columnName,
        availableSigns,
        valueType,
        isExactOrFirstInRange,
        sign,
        value,
        enumMap,
        onSignChange,
        onValueChange,
        onInputEnter: onParentInputEnter,
        error,
        errorMessage,
    } = props;

    const signOptions: DropdownItemProps[] = availableSigns.map((x) => ({
        key: x,
        value: x,
        text: signTextMap[x],
        className: 'comparison-' + x,
    }));

    const isValid = isComparisonValueValid(valueType, value);

    const finalError = isValid ? error : true;
    const finalErrorMessage = isValid
        ? errorMessage
        : computeErrorMessage(valueType);

    const onInputEnter = () => {
        if (isValid) {
            onParentInputEnter();
        }
    };

    return (
        <div className={classnames('ComparisonControl', finalError && 'error')}>
            <div className="input-with-select">
                {finalErrorMessage && (
                    <div className="error-message">{finalErrorMessage}</div>
                )}
                <Dropdown
                    className="sign"
                    options={signOptions}
                    value={sign}
                    onChange={onSignChange}
                />
                {(() => {
                    switch (valueType) {
                        case ValueType.STRING:
                            return (
                                <StringControl
                                    columnName={columnName}
                                    value={value as string}
                                    onChange={onValueChange}
                                    onInputEnter={onInputEnter}
                                />
                            );
                        case ValueType.NUMBER:
                            return (
                                <NumberControl
                                    columnName={columnName}
                                    value={value as string}
                                    onChange={onValueChange}
                                    onInputEnter={onInputEnter}
                                />
                            );
                        case ValueType.TIME: {
                            const hours = value ? (value as string[])[0] : '';
                            const minutes = value ? (value as string[])[1] : '';
                            return (
                                <TimeControl
                                    hours={hours}
                                    minutes={minutes}
                                    onChange={onValueChange}
                                    onInputEnter={onInputEnter}
                                />
                            );
                        }
                        case ValueType.DATETIME:
                        case ValueType.DATE: {
                            return (
                                <DateControl
                                    value={
                                        value as SupportedDateComparisonValue
                                    }
                                    sign={sign}
                                    showToday={isExactOrFirstInRange}
                                    showSelectRange={sign === ExactSign.EQ}
                                    onChange={onValueChange}
                                />
                            );
                        }
                        case ValueType.ENUM:
                            return (
                                <EnumControl
                                    enumMap={enumMap!}
                                    value={value as string}
                                    onChange={onValueChange}
                                />
                            );
                        default:
                            return null;
                    }
                })()}
            </div>
        </div>
    );
}

function computeErrorMessage(valueType: FilterValueType) {
    switch (valueType) {
        case ValueType.NUMBER:
            return 'Please enter a numeric value';
        case ValueType.TIME:
            return 'Please enter a valid time in the format HH MM';
        default:
            return '';
    }
}
